import { toast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { Check, Copy } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Label } from '../shared/Label/Label'

interface ProductUuidBadgeProps {
  uuid?: string
  label?: string
  /**
   * `field` acompanha os inputs do formulário.
   * `inline` é o chip do cabeçalho do produto, onde um campo largo destoa do título.
   */
  variant?: 'field' | 'inline'
}

/**
 * Identificador usado nas chamadas da API pública.
 * O valor fica legível e o botão ao lado copia.
 */
export default function ProductUuidBadge({
  uuid,
  label,
  variant = 'field',
}: ProductUuidBadgeProps) {
  const { t } = useTranslation(['common'])
  const [hasCopied, setHasCopied] = useState(false)
  const resetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (resetTimeout.current) clearTimeout(resetTimeout.current)
    }
  }, [])

  if (!uuid) return null

  const fieldLabel = label ?? t('common:productUuidLabel')
  const copyLabel = `${t('common:productUuidCopyAction')}: ${uuid}`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(uuid)
      setHasCopied(true)

      if (resetTimeout.current) clearTimeout(resetTimeout.current)
      resetTimeout.current = setTimeout(() => setHasCopied(false), 2000)

      toast({
        variant: 'success',
        description: t('common:productUuidCopied'),
      })
    } catch {
      toast({
        variant: 'error',
        description: t('common:errorCopying'),
      })
    }
  }

  const copyIcon = hasCopied ? (
    <Check className="size-4 text-primary" />
  ) : (
    <Copy className="size-4" />
  )

  if (variant === 'inline') {
    return (
      <button
        type="button"
        onClick={handleCopy}
        title={t('common:productUuidCopyAction')}
        aria-label={copyLabel}
        data-cy="product-uuid-badge"
        className="inline-flex max-w-full items-center gap-2 rounded-full border border-colorBorder bg-bgGray py-1.5 pl-3 pr-2 text-left transition-colors hover:border-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <span className="shrink-0 text-[11px] text-muted">{fieldLabel}</span>
        <span className="h-3 w-px shrink-0 bg-colorBorder" aria-hidden />
        <span className="min-w-0 truncate font-mono text-xs text-colorText">
          {uuid}
        </span>
        <span
          className={cn(
            'flex size-6 shrink-0 items-center justify-center text-muted',
            hasCopied && 'text-primary'
          )}
        >
          {copyIcon}
        </span>
      </button>
    )
  }

  return (
    <div className="grid w-full items-center gap-2">
      {/* O Label compartilhado junta className sem espaço. */}
      <Label className=" text-colorText">{fieldLabel}</Label>
      <div className="flex h-10 w-full items-center gap-2 rounded-md border border-colorBorder bg-bgDark pl-3 pr-1">
        <span className="min-w-0 flex-1 truncate font-mono text-sm text-colorText">
          {uuid}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          title={t('common:productUuidCopyAction')}
          aria-label={copyLabel}
          data-cy="product-uuid-badge"
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-md text-muted transition-colors hover:bg-white/5 hover:text-colorText focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            hasCopied && 'text-primary'
          )}
        >
          {copyIcon}
        </button>
      </div>
    </div>
  )
}
